import React, { useEffect, useMemo, useRef, useState } from "react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db, auth } from "../../firebase";
import { colors, spacing, typography } from "../../utils/designTokens";
import { useUser } from "../../context/UserContext";
import { useToast } from "../../components/Toast";
import { PERSONALES, buildPersonalHtml, buildFirmaTabla } from "../../utils/materialesPersonales";
import { topbarStyle, backBtn, ghostBtn, primaryBtn } from "./materialesUi";
import { FaArrowLeft, FaPrint, FaRegCopy, FaCheck, FaCode } from "react-icons/fa";

// Perfil del comercial logueado (doc `users`): nombre, celular y email reales.
// Cache a nivel módulo — se lee una vez por sesión.
let _perfilCache = null;
async function fetchPerfil(email) {
  if (_perfilCache && _perfilCache.email === email) return _perfilCache;
  const snap = await getDocs(query(collection(db, "users"), where("email", "==", email)));
  const docs = snap.docs.map((d) => d.data());
  // Prioriza el doc comercial (typeuser 4, tolera string); si no, el primero.
  const u = docs.find((x) => Number(x.typeuser) === 4) || docs[0] || {};
  _perfilCache = {
    email,
    name: u.name || `${u.NombreCompleto || ""} ${u.ApellidosCompleto || ""}`.trim(),
    tel: u.celular || "",
  };
  return _perfilCache;
}

/**
 * Visor de materiales personales (cartas A/B/C, tarjeta, firma): se completan
 * SOLOS con los datos del usuario logueado — nombre, celular y email del doc
 * `users`. Acciones: Imprimir (cartas/tarjeta), Copiar firma, Descargar HTML.
 */
export default function MaterialPersonalViewer({ material, isMobile, onClose }) {
  const { showToast } = useToast();
  const { userName, userEmail } = useUser();
  const email = (userEmail || auth.currentUser?.email || "").toLowerCase();
  const iframeRef = useRef(null);
  const [perfil, setPerfil] = useState(_perfilCache && _perfilCache.email === email ? _perfilCache : null);
  const [copiado, setCopiado] = useState(false);

  const meta = PERSONALES.find((p) => p.subtipo === material.subtipo) || PERSONALES[0];
  const esFirma = meta.subtipo === "firma";

  useEffect(() => {
    let alive = true;
    fetchPerfil(email)
      .then((p) => { if (alive) setPerfil(p); })
      .catch(() => { if (alive) setPerfil({ email, name: userName || "", tel: "" }); });
    return () => { alive = false; };
  }, [email, userName]);

  const agente = useMemo(() => ({
    name: perfil?.name || userName || "",
    email,
    tel: perfil?.tel || "",
    cargo: "Comercial",
  }), [perfil, userName, email]);

  const html = useMemo(() => buildPersonalHtml(meta.subtipo, agente), [meta.subtipo, agente]);

  const fitIframe = () => {
    try {
      const h = iframeRef.current?.contentDocument?.body?.scrollHeight;
      if (h) iframeRef.current.style.height = `${h + 24}px`;
    } catch (_) {}
  };

  // Impresión en ventana propia: sale vectorial (mejor que PDF por captura).
  function imprimir() {
    const w = window.open("", "_blank");
    if (!w) { showToast("El navegador bloqueó la ventana de impresión.", "error"); return; }
    w.document.write(html);
    w.document.close();
    setTimeout(() => { try { w.focus(); w.print(); } catch (_) {} }, 600);
  }

  async function copiarFirma() {
    try {
      const tmp = document.createElement("div");
      tmp.style.position = "fixed";
      tmp.style.left = "-10000px";
      tmp.innerHTML = buildFirmaTabla(agente);
      document.body.appendChild(tmp);
      const range = document.createRange();
      range.selectNodeContents(tmp);
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
      document.execCommand("copy");
      sel.removeAllRanges();
      tmp.remove();
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
      showToast("Firma copiada. Pegala en Gmail → Configuración → Firma.", "success");
    } catch {
      showToast("No se pudo copiar la firma.", "error");
    }
  }

  function descargarHtml() {
    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    const u = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = u; a.download = `habitia-${meta.subtipo.replace(/_/g, "-")}.html`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(u), 1000);
    showToast("HTML descargado.", "success");
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      <div style={topbarStyle}>
        <button onClick={onClose} style={backBtn} aria-label="Volver a materiales"><FaArrowLeft size={12} /> {isMobile ? "" : "Materiales"}</button>
        <div style={{ flex: 1, minWidth: 0, fontSize: typography.sizes.base, color: colors.gray900, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {meta.nombre}
        </div>
        {esFirma ? (
          <button onClick={copiarFirma} style={primaryBtn}>
            {copiado ? <FaCheck size={12} /> : <FaRegCopy size={12} />} {copiado ? "Copiada" : "Copiar firma"}
          </button>
        ) : (
          <button onClick={imprimir} style={primaryBtn}><FaPrint size={12} /> {isMobile ? "" : "Imprimir"}</button>
        )}
        <button onClick={descargarHtml} style={ghostBtn} aria-label="Descargar HTML"><FaCode size={12} /> {isMobile ? "" : "HTML"}</button>
      </div>
      <div style={{ padding: `${spacing[2]}px ${spacing[3]}px`, background: colors.primaryLighter, color: colors.primary, fontSize: typography.sizes.sm, flexShrink: 0 }}>
        Se completa solo con tus datos: {agente.name || "…"}{agente.tel ? ` · ${agente.tel}` : ""} · {agente.email}.
        {!perfil ? " Cargando tu perfil…" : (!agente.tel ? " No tenés celular cargado — pedile a un superadmin que lo agregue en Gestión de comerciales." : "")}
      </div>
      <div style={{ flex: 1, minWidth: 0, minHeight: 0, overflow: "auto", background: "#E9EBEE", padding: isMobile ? 10 : spacing[4] }}>
        <iframe ref={iframeRef} title={meta.nombre} srcDoc={html} onLoad={fitIframe} scrolling="no"
          style={{ width: "100%", maxWidth: 900, margin: "0 auto", border: 0, display: "block", background: "#ffffff" }} />
      </div>
    </div>
  );
}
